import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { LocationLabels } from "@mbb/config";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { ApiError, apiFetch } from "../../lib/api";

type ApplicationSummary = {
  id: string;
  fullName: string;
  preferredName: string | null;
  email: string;
  location: keyof typeof LocationLabels;
  photoUrl: string | null;
  createdAt: string;
  reviewedAt: string | null;
  userId: string;
  userStatus: "PENDING" | "REJECTED" | "APPROVED_UNPAID" | "ACTIVE" | "LAPSED";
};

const FILTERS: Array<{ value: ApplicationSummary["userStatus"]; label: string }> = [
  { value: "PENDING", label: "Pending" },
  { value: "APPROVED_UNPAID", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
];

export default function AdminApplications() {
  const [params, setParams] = useSearchParams();
  const status = (params.get("status") as ApplicationSummary["userStatus"]) || "PENDING";

  const [items, setItems] = useState<ApplicationSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    setError(null);
    apiFetch<{ applications: ApplicationSummary[] }>(
      `/api/applications?status=${status}`,
    )
      .then((data) => {
        if (!cancelled) setItems(data.applications);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof ApiError ? err.message : "Failed to load applications.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [status]);

  return (
    <>
      <Header />
      <main className="container section" style={{ minHeight: "60vh" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "end",
            flexWrap: "wrap",
            gap: "1rem",
            marginBottom: "1.5rem",
          }}
        >
          <div>
            <div className="eyebrow" style={{ color: "var(--c-coral)" }}>
              Admin
            </div>
            <h1
              className="display display--lg"
              style={{ marginTop: "0.5rem", marginBottom: 0 }}
            >
              applications.
            </h1>
          </div>
          <div
            role="tablist"
            aria-label="Filter applications by status"
            style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}
          >
            {FILTERS.map((f) => {
              const active = f.value === status;
              return (
                <button
                  key={f.value}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setParams({ status: f.value })}
                  style={{
                    padding: "0.5rem 0.95rem",
                    borderRadius: "var(--r-pill)",
                    border: `1.5px solid ${active ? "var(--c-palm)" : "var(--c-rule)"}`,
                    background: active ? "var(--c-palm)" : "transparent",
                    color: active ? "var(--c-sand)" : "var(--c-palm)",
                    fontFamily: "var(--f-label)",
                    textTransform: "uppercase",
                    letterSpacing: "0.12em",
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>

        {error && (
          <p role="alert" style={{ color: "#9a3514" }}>
            {error}
          </p>
        )}

        {!items ? (
          <p className="eyebrow">Loading…</p>
        ) : items.length === 0 ? (
          <p style={{ color: "var(--c-palm)", opacity: 0.75 }}>
            Nothing here right now.
          </p>
        ) : (
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: 0,
              display: "grid",
              gap: "0.85rem",
            }}
          >
            {items.map((a) => (
              <li key={a.id}>
                <Link
                  to={`/admin/applications/${a.id}`}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "56px 1fr auto",
                    gap: "1rem",
                    alignItems: "center",
                    padding: "1rem 1.1rem",
                    background: "#fff",
                    border: "1px solid var(--c-rule)",
                    borderRadius: "var(--r-md)",
                  }}
                >
                  {a.photoUrl ? (
                    <img
                      src={a.photoUrl}
                      alt=""
                      width={56}
                      height={56}
                      style={{
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <div
                      aria-hidden="true"
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: "50%",
                        background: "var(--c-blush)",
                      }}
                    />
                  )}
                  <div>
                    <div
                      style={{
                        fontWeight: 600,
                        color: "var(--c-palm)",
                        marginBottom: 2,
                      }}
                    >
                      {a.preferredName ?? a.fullName}
                      {a.preferredName && (
                        <span style={{ opacity: 0.6, fontWeight: 400 }}>
                          {" "}
                          · {a.fullName}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: "0.85rem", opacity: 0.75 }}>
                      {a.email} · {LocationLabels[a.location]} ·{" "}
                      {new Date(a.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <span
                    className="eyebrow"
                    style={{ color: "var(--c-coral)", fontSize: "0.7rem" }}
                  >
                    Open →
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </main>
      <Footer />
    </>
  );
}
